/* TARGET LAUNCHING AGAINST YEAR, SEASON AND TODAY — Jay, task "Project Tracker — validate Target Launching", 01/11:
   PRJ-00202 (27SS Jinya x Helinox) was saved as Year 2027, Spring/Summer, Target Launching 05/2026 — a month already
   gone and a year before its season, almost certainly 2026 typed for 2027. The CEO decides go / no-go on these rows.

   Two rules, one function, used by every form that edits a project (New Project, Edit, Project Approval's Edit):
   - BLOCK a Target Launching before the current month (Vietnam time).
   - WARN when it sits outside the season's usual window; the person ticks that it is right, then saves.
   Both look only at what THIS save changes: a project saved long ago with a launch now in the past, or one that never
   fitted the rule, can still be edited for anything else. ASAP and a blank month are not checked.

   THE WINDOWS were read off the 70 projects that carry a year, a season and a month (01/11): Spring launched
   Jan–Apr, Spring/Summer Jan–Jun, Summer Jun–Jul, Fall/Winter Jul–Mar of the next year. Each season part owns a
   stretch of months counted from January of the season's year (1 = Jan, 13 = Jan of the next year); a pick of two
   parts takes both; a Year with no part takes the whole year. Three months either side are allowed for early or late
   launches, so only a date that is clearly in another year is questioned — PRJ-00202 is 7 months before its window. */

export const SEASON_LAUNCH_MONTHS = { Spring: [1, 4], Summer: [4, 7], Fall: [7, 10], Winter: [10, 15] };
export const LAUNCH_TOLERANCE_MONTHS = 3;

const PARTS = ['Spring', 'Summer', 'Fall', 'Winter'];
const partsOf = (raw) => {
  const list = Array.isArray(raw) ? raw : String(raw || '').split(',');
  const want = new Set(list.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean));
  return PARTS.filter((p) => want.has(p.toLowerCase()));
};
const isMonth = (v) => /^\d{4}-(0[1-9]|1[0-2])$/.test(String(v || ''));
// months since year 0, so two months compare as numbers
const ord = (ym) => Number(ym.slice(0, 4)) * 12 + Number(ym.slice(5, 7)) - 1;
const ymOf = (n) => `${Math.floor(n / 12)}-${String((n % 12) + 1).padStart(2, '0')}`;

export const currentMonthVN = (now = new Date()) => new Date(now.getTime() + 7 * 3600000).toISOString().slice(0, 7);

/** The months a launch for this Year + season is expected in, tolerance included: { from: 'YYYY-MM', to: 'YYYY-MM' }. */
export const launchWindow = (seasonYear, seasonParts) => {
  const y = Number(seasonYear) || 0;
  if (!y) return null;
  const parts = partsOf(seasonParts);
  const spans = parts.length ? parts.map((p) => SEASON_LAUNCH_MONTHS[p]) : [[1, 12]];
  const lo = Math.min(...spans.map((s) => s[0])) - LAUNCH_TOLERANCE_MONTHS;
  const hi = Math.max(...spans.map((s) => s[1])) + LAUNCH_TOLERANCE_MONTHS;
  const jan = y * 12; // January of the season's year as an ordinal, minus the 1 the spans count from
  return { from: ymOf(jan + lo - 1), to: ymOf(jan + hi - 1) };
};

/** What the save would trip on. `before` is the project as stored (null for a new one).
 *  { past, mismatch, window, signature } — `signature` is what a confirmation is tied to, so ticking "it is right"
 *  stops counting the moment the year, the season or the month is changed again. */
export const launchCheck = (value, before = null, now = new Date()) => {
  const launch = String(value?.targetLaunchMonth || '');
  const year = value?.seasonYear ? String(value.seasonYear) : '';
  const parts = partsOf(value?.seasonParts).join(',');
  const signature = `${year}|${parts}|${launch}`;
  const out = { past: false, mismatch: false, window: null, signature };
  if (!isMonth(launch)) return out;
  const launchChanged = !before || String(before.targetLaunchMonth || '') !== launch;
  const seasonChanged = !before || String(before.seasonYear || '') !== year || partsOf(before.seasonParts).join(',') !== parts;
  if (launchChanged && ord(launch) < ord(currentMonthVN(now))) out.past = true;
  const win = launchWindow(year, parts);
  if (win && (launchChanged || seasonChanged) && (ord(launch) < ord(win.from) || ord(launch) > ord(win.to))) {
    out.mismatch = true;
    out.window = win;
  }
  return out;
};

/** The same two questions about a project as it stands — for a screen that only reads it (Project Approval's list). */
export const launchFlags = (g, now = new Date()) => {
  const launch = String(g?.targetLaunchMonth || '');
  if (!isMonth(launch)) return { past: false, mismatch: false, window: null };
  const win = launchWindow(g?.seasonYear, g?.seasonParts);
  return {
    past: ord(launch) < ord(currentMonthVN(now)),
    mismatch: !!win && (ord(launch) < ord(win.from) || ord(launch) > ord(win.to)),
    window: win,
  };
};

/** The form's blocking messages, from a check: [] when the save may go. */
export const launchErrors = (check, confirmed, t) => [
  check.past && t('stx.v.launchPast'),
  check.mismatch && confirmed !== check.signature && t('stx.v.launchConfirm'),
].filter(Boolean);
